import moment from 'moment';
import queryString from 'query-string';
import { createSearchParams } from 'react-router-dom';
import Immutable from 'seamless-immutable';

// Locals
import statsApi from '~/api/stats';
import getEnvironmentsToPathName from '~/common/helpers/getEnvironmentsToPathName';
import getRealmToPathName from '~/common/helpers/getRealmToPathName';

const convertApiTimeToInterval = apiTime => {
  const timeMap = {
    '15 Minutes': '15m',
    '1 Hour': '1h',
    '6 Hours': '6h',
    '1 Day': '1d',
    '7 Days': '7d'
  };
  return timeMap[apiTime] || apiTime;
};

const getPropsSearch = () => {
  const params = queryString.parse(window.location.search.replace('?', ''));
  const STRING_EMPTY = '';
  const SPECIFIC_DATE_FROM_DEFAULT = new Date();

  let interval = '15m';
  let dateFrom = moment().subtract(15, 'minutes').toISOString();
  let dateTo = moment().toISOString();

  if (params.time && params.dateFrom && params.dateTo) {
    interval = convertApiTimeToInterval(params.time);
    dateFrom = decodeURIComponent(params.dateFrom);
    dateTo = decodeURIComponent(params.dateTo);
  } else if (params.interval) {
    interval = params.interval;
  }

  const timeValue = parseInt(interval.replace(/\D/g, ''), 10);

  return {
    pipelineName: params.pipelineName || STRING_EMPTY,
    dateTo,
    dateFrom,
    interval,
    label: timeValue,
    name: interval,
    percentile: params.percentile || 'avg',
    sortBy: params.sortBy || STRING_EMPTY,
    direction: params.direction || 'default',
    specific: {
      dateFrom: params.dateFrom
        ? new Date(decodeURIComponent(params.dateFrom))
        : SPECIFIC_DATE_FROM_DEFAULT,
      dateTo: params.dateTo
        ? new Date(decodeURIComponent(params.dateTo))
        : SPECIFIC_DATE_FROM_DEFAULT
    }
  };
};

const initialState = Immutable({
  loading: true,
  loadingMetrics: false,
  realm: getRealmToPathName(window.location.pathname),
  data: null,
  error: false,
  environment: getEnvironmentsToPathName(window.location.pathname),
  interval: getPropsSearch(),
  pipeline: '',
  specific: getPropsSearch().specific,
  pipelineName: getPropsSearch().pipelineName,
  percentile: getPropsSearch().percentile || 'avg',
  sortBy: getPropsSearch().sortBy,
  direction: getPropsSearch().direction,
  ref: null,
  pipelineDetails: null
});

const statsModel = {
  name: 'stats',
  state: initialState,
  reducers: {
    setLoading(state, payload) {
      return state.merge({
        loading: payload
      });
    },
    setLoadingMetrics(state, payload) {
      return state.merge({
        loadingMetrics: payload
      });
    },
    setInterval(state, payload) {
      return state.merge({
        interval: payload
      });
    },
    setSpecific(state, payload) {
      return state.merge({
        specific: payload
      });
    },
    setPipeline(state, payload) {
      return state.merge({
        pipeline: payload
      });
    },
    setRef(state, payload) {
      return state.merge({
        ref: payload
      });
    },
    setData(state, payload) {
      return state.merge({
        data: payload
      });
    },
    setError(state, payload) {
      return state.merge({
        error: payload
      });
    },
    setPipelineName(state, payload) {
      return state.merge({
        pipelineName: payload
      });
    },
    setPercentile(state, payload) {
      return state.merge({
        percentile: payload
      });
    },
    setSortBy(state, payload) {
      return state.merge({
        sortBy: payload
      });
    },
    setDirection(state, payload) {
      return state.merge({
        direction: payload
      });
    },
    setPipelineDetails(state, payload) {
      return state.merge({ pipelineDetails: payload });
    },
    reset() {
      return initialState;
    }
  },
  effects: dispatch => ({
    async find(
      { environment, realm, all, pipelineName, interval, dateFrom, dateTo },
      state
    ) {
      try {
        dispatch.stats.setLoading(true);

        if (pipelineName !== undefined) {
          dispatch.stats.setPipelineName(pipelineName);
        }

        const search = {
          ...state.stats.interval,
          ...(interval && { interval }),
          ...(dateFrom && { dateFrom }),
          ...(dateTo && { dateTo }),
          pipelineName:
            pipelineName !== undefined
              ? pipelineName
              : state.stats.pipelineName || '',
          ...(state.stats.percentile !== 'avg' && {
            percentile: state.stats.percentile || '50'
          }),
          sortBy: state.stats.sortBy || '',
          direction: state.stats.direction || 'default',
          refreshInterval:
            new URLSearchParams(window.location.search).get(
              'refreshInterval'
            ) || 'off'
        };

        const currentParams = queryString.parse(window.location.search);
        if (currentParams.selectedPipeline) {
          search.selectedPipeline = currentParams.selectedPipeline;
        }

        dispatch.router.navigate({
          pathname: window.location.pathname,
          search: `?${createSearchParams(search)}`,
          options: { replace: true }
        });

        const payload = {
          realm,
          environment,
          interval: interval || state.stats.interval.interval,
          dateFrom: dateFrom || state.stats.interval.dateFrom,
          dateTo: dateTo || state.stats.interval.dateTo,
          pipelineName:
            pipelineName !== undefined
              ? pipelineName
              : state.stats.pipelineName || '',
          percentile:
            state.stats.percentile === 'avg'
              ? undefined
              : state.stats.percentile || '50',
          all,
          token: state.authentication?.userData?.token
        };
        try {
          const request = await statsApi.find(payload);

          if (request?.data?.stats) {
            dispatch.stats.setData(request.data.stats);
          } else {
            dispatch.stats.setData({
              runtimeStats: { dateHistogramStats: [] }
            });
          }
        } catch (error) {
          dispatch.stats.setError(true);
        }

        dispatch.stats.setLoading(false);
      } catch (e) {
        dispatch.stats.setError(true);
        dispatch.stats.setLoading(false);
      }
    },
    findPipelineDetails: async (
      {
        realm,
        environment,
        interval,
        dateFrom,
        dateTo,
        pipelineName,
        percentile,
        all
      },
      state
    ) => {
      try {
        if (
          !pipelineName ||
          pipelineName === '' ||
          pipelineName === 'undefined'
        ) {
          return;
        }

        dispatch.stats.setLoading(true);

        const payload = {
          realm,
          environment,
          interval,
          dateFrom,
          dateTo,
          pipelineName,
          percentile: percentile === 'avg' ? undefined : percentile,
          all,
          token: state.authentication?.userData?.token
        };

        const response = await statsApi.find(payload);

        if (response?.data?.stats) {
          dispatch.stats.setPipelineDetails(response.data.stats);
        } else {
          dispatch.stats.setPipelineDetails({
            runtimeStats: { dateHistogramStats: [] }
          });
        }
      } catch (error) {
        dispatch.stats.setError(true);
      } finally {
        dispatch.stats.setLoading(false);
      }
    }
  })
};

export default statsModel;
